import type { AxiosProgressEvent } from "axios";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  BookResponse,
  CustomBookResponse,
  CustomBookInput,
  CustomMaterialUsage,
  LessonResponse,
  SchoolBookInput,
  SchoolLessonResponse,
  SchoolLessonListResponse,
} from "~/types/content";

class ContentModule extends HttpFactory {
  async fetchBooks(): Promise<BookResponse[]> {
    return await this.get(`${ENDPOINTS.CONTENT.BOOKS}`);
  }

  async fetchCustomBooks(): Promise<CustomBookResponse[]> {
    return await this.get(`${ENDPOINTS.CONTENT.CUSTOM_BOOKS}`);
  }

  async fetchCustomMaterialUsage(): Promise<CustomMaterialUsage> {
    return await this.get(`${ENDPOINTS.CONTENT.CUSTOM_BOOKS}usage/`);
  }

  async fetchCustomBook(bookId: number): Promise<CustomBookResponse> {
    return await this.get(`${ENDPOINTS.CONTENT.CUSTOM_BOOKS}${bookId}/`);
  }

  async createCustomBook(data: CustomBookInput): Promise<CustomBookResponse> {
    return await this.post(ENDPOINTS.CONTENT.CUSTOM_BOOKS, data);
  }

  async updateCustomBook(
    bookId: number,
    data: CustomBookInput,
  ): Promise<CustomBookResponse> {
    return await this.patch(
      `${ENDPOINTS.CONTENT.CUSTOM_BOOKS}${bookId}/`,
      data,
    );
  }

  async deleteCustomBook(bookId: number): Promise<void> {
    return await this.delete(`${ENDPOINTS.CONTENT.CUSTOM_BOOKS}${bookId}/`);
  }

  async fetchCustomLessons(
    bookId: number,
    page = 1,
    pageSize = 10,
  ): Promise<SchoolLessonListResponse> {
    return await this.get(ENDPOINTS.CONTENT.CUSTOM_LESSONS, {
      lessonplan: bookId,
      page,
      pageSize,
    });
  }

  async createCustomLesson(
    data: FormData,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ): Promise<SchoolLessonResponse> {
    return await this.post(ENDPOINTS.CONTENT.CUSTOM_LESSONS, data, {
      onUploadProgress,
    });
  }

  async updateCustomLesson(
    lessonId: number,
    data: FormData,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ): Promise<SchoolLessonResponse> {
    return await this.patch(
      `${ENDPOINTS.CONTENT.CUSTOM_LESSONS}${lessonId}/`,
      data,
      { onUploadProgress },
    );
  }

  async retryCustomLessonProcessing(
    lessonId: number,
  ): Promise<SchoolLessonResponse> {
    return await this.post(
      `${ENDPOINTS.CONTENT.CUSTOM_LESSONS}${lessonId}/retry-processing/`,
      {},
    );
  }

  async deleteCustomLesson(lessonId: number): Promise<void> {
    return await this.delete(`${ENDPOINTS.CONTENT.CUSTOM_LESSONS}${lessonId}/`);
  }

  async fetchSchoolBooks(): Promise<BookResponse[]> {
    return await this.get(`${ENDPOINTS.CONTENT.SCHOOL_BOOKS}`);
  }

  async createSchoolBook(data: SchoolBookInput): Promise<BookResponse> {
    return await this.post(`${ENDPOINTS.CONTENT.SCHOOL_BOOKS}`, data);
  }

  async fetchSchoolBook(bookId: number): Promise<BookResponse> {
    return await this.get(`${ENDPOINTS.CONTENT.SCHOOL_BOOKS}${bookId}/`);
  }

  async updateSchoolBook(
    bookId: number,
    data: SchoolBookInput,
  ): Promise<BookResponse> {
    return await this.patch(
      `${ENDPOINTS.CONTENT.SCHOOL_BOOKS}${bookId}/`,
      data,
    );
  }

  async fetchSchoolLessons(
    bookId: number,
    page = 1,
  ): Promise<SchoolLessonListResponse> {
    return await this.get(ENDPOINTS.CONTENT.SCHOOL_LESSONS, {
      lessonplan: bookId,
      page,
    });
  }

  async createSchoolLesson(
    data: FormData,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ): Promise<SchoolLessonResponse> {
    return await this.post(ENDPOINTS.CONTENT.SCHOOL_LESSONS, data, {
      onUploadProgress,
    });
  }

  async updateSchoolLesson(
    lessonId: number,
    data: FormData,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ): Promise<SchoolLessonResponse> {
    return await this.patch(
      `${ENDPOINTS.CONTENT.SCHOOL_LESSONS}${lessonId}/`,
      data,
      { onUploadProgress },
    );
  }

  async fetchBook(bookId: number): Promise<BookResponse> {
    return await this.get(`${ENDPOINTS.CONTENT.BOOKS}${bookId}/`);
  }

  async fetchLessons(lessonplanId: number): Promise<LessonResponse[]> {
    return await this.get(
      `${ENDPOINTS.CONTENT.LESSONS.replace(
        "{lessonplanId}",
        lessonplanId.toString(),
      )}`,
    );
  }
}

export default ContentModule;
